import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CalendarDays, Download, Loader2, RefreshCw, Search, Truck } from "lucide-react";
import { toast } from "sonner";

import DataSourceNotice from "@/components/DataSourceNotice";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const localDate = (date = new Date()) => {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

const initialFilters = () => {
  const today = localDate();
  return {
    from_date: today,
    to_date: today,
    search: "",
    plant: "all",
    shipping_point: "all",
    status: "all",
  };
};

const quantity = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return Number(value).toLocaleString(undefined, { maximumFractionDigits: 3 });
};

const statusStyle = {
  "Fully dispatched": "border-emerald-200 bg-emerald-50 text-emerald-700",
  "Partially dispatched": "border-amber-200 bg-amber-50 text-amber-700",
  Dispatched: "border-blue-200 bg-blue-50 text-blue-700",
  Planned: "border-slate-200 bg-slate-50 text-slate-700",
};

const DailyDispatch = () => {
  const [draft, setDraft] = useState(initialFilters);
  const [filters, setFilters] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const requestParams = useMemo(() => ({
    ...filters,
    plant: filters.plant === "all" ? undefined : filters.plant,
    shipping_point: filters.shipping_point === "all" ? undefined : filters.shipping_point,
    status: filters.status === "all" ? undefined : filters.status,
    search: filters.search || undefined,
  }), [filters]);

  const fetchDispatches = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/supply-chain-agent/daily-dispatch`, {
        params: { ...requestParams, page, page_size: 50 },
      });
      setData(response.data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to load daily dispatch details");
    } finally {
      setLoading(false);
    }
  }, [page, requestParams]);

  useEffect(() => { fetchDispatches(); }, [fetchDispatches]);

  const applyFilters = (event) => {
    event.preventDefault();
    if (draft.from_date > draft.to_date) {
      toast.error("From date cannot be after to date");
      return;
    }
    setPage(1);
    setFilters(draft);
  };

  const resetFilters = () => {
    const next = initialFilters();
    setDraft(next);
    setFilters(next);
    setPage(1);
  };

  const exportReport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/supply-chain-agent/export/daily-dispatch`, {
        params: requestParams,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `daily_dispatch_${filters.from_date}_${filters.to_date}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Daily dispatch report downloaded");
    } catch (error) {
      toast.error("Daily dispatch export failed");
    } finally {
      setExporting(false);
    }
  };

  const summary = data?.summary || {};
  const pagination = data?.pagination || { page: 1, pages: 0, total: 0 };
  const rows = data?.dispatches || [];

  return (
    <div className="space-y-4" data-testid="daily-dispatch-page">
      <DataSourceNotice source={data?.data_source} message={data?.message} showLive />

      <form onSubmit={applyFilters} className="rounded-lg border bg-card p-3 space-y-3" data-testid="daily-dispatch-filters">
        <div className="flex flex-wrap items-end gap-2">
          <label className="space-y-1 text-xs text-muted-foreground">
            <span>Dispatch date from</span>
            <Input type="date" className="h-9 w-[150px]" value={draft.from_date} onChange={(e) => setDraft((v) => ({ ...v, from_date: e.target.value }))} required />
          </label>
          <label className="space-y-1 text-xs text-muted-foreground">
            <span>Dispatch date to</span>
            <Input type="date" className="h-9 w-[150px]" value={draft.to_date} onChange={(e) => setDraft((v) => ({ ...v, to_date: e.target.value }))} required />
          </label>
          <label className="space-y-1 text-xs text-muted-foreground grow min-w-[210px] max-w-sm">
            <span>Search delivery, SO, customer or material</span>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input className="h-9 pl-8" value={draft.search} onChange={(e) => setDraft((v) => ({ ...v, search: e.target.value }))} placeholder="Search..." />
            </div>
          </label>
          <Select value={draft.plant} onValueChange={(value) => setDraft((v) => ({ ...v, plant: value }))}>
            <SelectTrigger className="w-[145px]"><SelectValue placeholder="All plants" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All plants</SelectItem>{(data?.filter_options?.plants || []).map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={draft.shipping_point} onValueChange={(value) => setDraft((v) => ({ ...v, shipping_point: value }))}>
            <SelectTrigger className="w-[170px]"><SelectValue placeholder="All shipping points" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All shipping points</SelectItem>{(data?.filter_options?.shipping_points || []).map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={draft.status} onValueChange={(value) => setDraft((v) => ({ ...v, status: value }))}>
            <SelectTrigger className="w-[175px]"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{(data?.filter_options?.statuses || []).map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}</SelectContent>
          </Select>
          <Button type="submit" className="h-9 bg-emerald-600 hover:bg-emerald-700" disabled={loading}>GO</Button>
          <Button type="button" variant="outline" size="icon" className="h-9 w-9" onClick={resetFilters} title="Reset filters"><RefreshCw className="h-4 w-4" /></Button>
          <Button type="button" variant="outline" className="h-9" onClick={exportReport} disabled={exporting || loading}>
            {exporting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />} Export
          </Button>
        </div>
        <p className="text-xs text-muted-foreground flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> Dispatch date is based on SAP Actual Goods Movement Date. Maximum range: 90 days.</p>
      </form>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {[
          ["Dispatch Lines", summary.dispatch_lines || 0],
          ["Delivery Documents", summary.delivery_documents || 0],
          ["Sales Orders", summary.sales_orders || 0],
          ["Fully Dispatched", summary.fully_dispatched || 0],
          ["Partially Dispatched", summary.partially_dispatched || 0],
        ].map(([label, value]) => (
          <Card key={label}><CardContent className="p-4"><p className="text-xs uppercase text-muted-foreground">{label}</p><p className="mt-1 text-2xl font-semibold">{value}</p></CardContent></Card>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex h-48 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-emerald-600" /></div>
          ) : rows.length === 0 ? (
            <div className="flex h-48 flex-col items-center justify-center text-center text-muted-foreground">
              <Truck className="mb-2 h-8 w-8" />
              <p className="font-medium">No daily dispatch records</p>
              <p className="text-xs">Change the date or filters, or check the SAP status shown above.</p>
            </div>
          ) : (
            <Table>
              <TableHeader><TableRow className="bg-muted/50">
                <TableHead>Date</TableHead><TableHead>Delivery / Item</TableHead><TableHead>Sales Order / Item</TableHead><TableHead>Customer</TableHead><TableHead>Material</TableHead><TableHead>Plant</TableHead><TableHead className="text-right">Order Qty</TableHead><TableHead className="text-right">Dispatch Qty</TableHead><TableHead className="text-right">Cumulative</TableHead><TableHead className="text-right">Pending</TableHead><TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>{rows.map((row) => (
                <TableRow key={`${row.delivery_document}-${row.delivery_item}`}>
                  <TableCell className="whitespace-nowrap">{row.dispatch_date || "—"}</TableCell>
                  <TableCell><div className="font-medium">{row.delivery_document || "—"}</div><div className="text-xs text-muted-foreground">Item {row.delivery_item || "—"}</div></TableCell>
                  <TableCell><div className="font-medium">{row.sales_order || "—"}</div><div className="text-xs text-muted-foreground">Item {row.sales_order_item || "—"}</div></TableCell>
                  <TableCell><div className="max-w-[180px] truncate font-medium" title={row.customer_name}>{row.customer_name || row.customer_number || "—"}</div>{row.customer_name && <div className="text-xs text-muted-foreground">{row.customer_number}</div>}</TableCell>
                  <TableCell><div className="font-medium">{row.material || "—"}</div><div className="max-w-[190px] truncate text-xs text-muted-foreground" title={row.material_description}>{row.material_description || ""}</div></TableCell>
                  <TableCell><div>{row.plant || "—"}</div><div className="text-xs text-muted-foreground">SP: {row.shipping_point || "—"}</div></TableCell>
                  <TableCell className="text-right tabular-nums">{quantity(row.order_quantity)}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{quantity(row.dispatch_quantity)} {row.unit}</TableCell>
                  <TableCell className="text-right tabular-nums">{quantity(row.cumulative_dispatch_quantity)}</TableCell>
                  <TableCell className="text-right tabular-nums">{quantity(row.pending_quantity)}</TableCell>
                  <TableCell><Badge variant="outline" className={statusStyle[row.status] || ""}>{row.status || "Unknown"}</Badge></TableCell>
                </TableRow>
              ))}</TableBody>
            </Table>
          )}
          <div className="flex items-center justify-between border-t px-3 py-2 text-sm text-muted-foreground">
            <span>{pagination.total || 0} dispatch line{pagination.total === 1 ? "" : "s"}</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={loading || page <= 1} onClick={() => setPage((value) => value - 1)}>Previous</Button>
              <span>Page {pagination.pages ? pagination.page : 0} of {pagination.pages || 0}</span>
              <Button variant="outline" size="sm" disabled={loading || page >= (pagination.pages || 0)} onClick={() => setPage((value) => value + 1)}>Next</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DailyDispatch;
